import { Star } from "@gigpro/ui/Icons";
import { Rating } from "@gigpro/ui/Rating";
import { getEmployeesInterviewsListQueryKey, useEmployeesInterviewsPartialUpdate } from "@sidegigllc/api/client";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useIsProW2, useLatestInterview } from "./utils";

type InterviewRatingProps = {
  proId: number;
};

const ratings: Record<number, string> = {
  1: "Lacks experience",
  2: "Unprofessional",
  3: "Below average",
  4: "Above average",
  5: "Absolute Rockstar",
};

export function InterviewRating({ proId }: Readonly<InterviewRatingProps>) {
  const queryClient = useQueryClient();
  const isW2Pro = useIsProW2(proId);
  const activeInterview = useLatestInterview(proId);
  const interviewRating = activeInterview.data?.rating;

  const updateInterview = useEmployeesInterviewsPartialUpdate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getEmployeesInterviewsListQueryKey({ pro: proId }) });
      },
    },
  });

  const [rating, setRating] = React.useState<number | null>(null);
  const [ratingHover, setRatingHover] = React.useState<number | null>(null);

  const onRatingChange = async (value: number | null) => {
    setRating(value);
    if (value == null) return;
    await updateInterview.mutateAsync({ id: activeInterview.data?.id ?? "", data: { rating: value } });
  };

  const finalRating = interviewRating ?? ratingHover ?? rating;

  if (!isW2Pro) return null;
  if (!activeInterview.data) {
    return <p className="text-center text-large font-semibold text-gray">No active interview found for this pro.</p>;
  }
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex gap-4">
        {Object.entries(ratings).map(([value, label]) => (
          <p key={value} className="flex items-center gap-1">
            {value} <Star className="size-3" /> {label}
          </p>
        ))}
      </div>
      <p className="text-extraLarge font-semibold">How would you rate this candidate overall?</p>
      <div className="flex items-center gap-2">
        <Rating
          size="large"
          readOnly={interviewRating != null || !activeInterview.data}
          value={interviewRating ?? rating}
          onValueChange={(value) => onRatingChange(value)}
          onHoverChange={(value) => setRatingHover(value === -1 ? null : value)}
        />
        <p className="w-24 font-semibold">{finalRating ? ratings[finalRating] : "Not rated yet"}</p>
      </div>
    </div>
  );
}
